import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Container, Typography, Paper, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';
import { getPaymentDetail } from './_apis/serviceCalls';
import ProjectCheckoutComponent from './ProjectCheckoutComponent';
import ParticipantCheckoutComponent from './ParticipantCheckoutComponent';
import { MotionContainer, varBounceIn } from '../../components/animate';
import Page from '../../components/Page';
import TravelCheckoutComponent from './TravelCheckoutComponent';
import TripCheckoutComponent from './TrripCheckoutComponent';

const PROJECT_MODE = 1;
const PARTICIPANT_MODE = 2;
const TRIP_MODE = 3;
const TRAVEL_MODE = 4;

const RootStyle = styled(Page)(({ theme }) => ({
  display: 'flex',
  minHeight: '100%',
  alignItems: 'center',
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(10)
}));

function PaymentLink({ dispatch }) {
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const { search } = useLocation();
  const id = new URLSearchParams(search).get('id');
  const token = new URLSearchParams(search).get('token');

  useEffect(() => {
    const params = { id, token };
    getPaymentDetail(
      params,
      (data) => {
        data.balance = data.amount;
        data.paramId = id;
        data.token = token;
        if (data?.project_id) {
          data.id = data.project_id;
          dispatch({ type: 'ADD_FEE', payload: data.amount });
          dispatch({ type: 'ADD_ITEM_CART_FROM_PROJECTS', payload: [data] });
          setMode(PROJECT_MODE);
        } else if (data?.travel_id && data?.is_trip_payment === 0) {
          // Travel mode: travel_id var ve is_trip_payment 0
          data.id = data.travel_id;
          data.name = `${data.first_name} ${data.last_name}`;
          dispatch({ type: 'ADD_FEE', payload: data.amount });
          dispatch({ type: 'ADD_ITEM_CART_FROM_TRAVELS', payload: [data] });
          setMode(TRAVEL_MODE);
        } else if (!data?.travel_id && data?.is_trip_payment === 1) {
          // Trip mode: travel_id null ve is_trip_payment 1
          data.id = data.participant_id;
          data.name = `${data.first_name} ${data.last_name}`;
          dispatch({ type: 'ADD_FEE', payload: data.amount });
          dispatch({ type: 'ADD_ITEM_CART_FROM_TRIPS', payload: [data] });
          setMode(TRIP_MODE);
        } else if (data?.participant_id && !data?.travel_id && data?.is_trip_payment === 0) {
          // Participant mode: participant_id var, travel_id null ve is_trip_payment 0
          data.id = data.participant_id;
          data.name = `${data.first_name} ${data.last_name}`;
          dispatch({ type: 'ADD_FEE', payload: data.amount });
          dispatch({ type: 'ADD_ITEM_CART_FROM_PARTICIPANTS', payload: [data] });
          setMode(PARTICIPANT_MODE);
        }
        setPaymentData(data);
      },
      (err) => {
        setError(err.message);
      }
    );
  }, []);

  return (
    <RootStyle title="Payment | Genius">
      <Container>
        <MotionContainer initial="initial" open>
          <Box sx={{ margin: 'auto', textAlign: 'center' }}>
            <motion.div variants={varBounceIn}>
              {paymentData ? (
                mode === PROJECT_MODE ? (
                  <ProjectCheckoutComponent />
                ) : mode === PARTICIPANT_MODE ? (
                  <ParticipantCheckoutComponent />
                ) : mode === TRAVEL_MODE ? (
                  <TravelCheckoutComponent />
                ) : mode === TRIP_MODE ? (
                  <TripCheckoutComponent />
                ) : null
              ) : error ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    maxWidth: 600,
                    mx: 'auto',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 3
                    }}
                  >
                    <ErrorOutlineIcon
                      sx={{
                        fontSize: 80,
                        color: 'white',
                        filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))'
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        textAlign: 'center',
                        textShadow: '0px 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      Oops!
                    </Typography>
                    <Alert
                      severity="error"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                          width: '100%',
                          textAlign: 'center'
                        }
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Please check your link or contact support team.
                      </Typography>
                    </Alert>
                  </Box>
                </Paper>
              ) : (
                <p>Loading...</p>
              )}
            </motion.div>
          </Box>
        </MotionContainer>
      </Container>
    </RootStyle>
  );
}

PaymentLink.propTypes = {
  dispatch: PropTypes.func.isRequired
};

export default connect(() => ({}))(PaymentLink);
